import { useState, useEffect } from 'react';
import { api } from '../utils/apiComponent/apiClient';
import { isDebugMode } from '../config/environment-configuration';

interface MaintenanceStatus {
  isInMaintenance: boolean;
  isChecking: boolean;
  lastChecked: Date | null;
  checkCount: number;
}

/**
 * Hook pour gérer le mode maintenance
 */
export function useMaintenanceMode(autoCheckInterval: number = 30000) {
  const [status, setStatus] = useState<MaintenanceStatus>({
    isInMaintenance: true, // Démarrer directement en maintenance
    isChecking: false,
    lastChecked: null,
    checkCount: 0
  });

  /**
   * Vérifie si le serveur est toujours en maintenance
   */
  const checkMaintenanceStatus = async (): Promise<boolean> => {
    setStatus(prev => ({ ...prev, isChecking: true }));

    try {
      // Faire un appel simple pour tester si le serveur répond
      await api.get('/auth/user'); // ou n'importe quel endpoint simple
      
      // Si on arrive ici, le serveur n'est plus en maintenance
      setStatus(prev => ({
        ...prev,
        isInMaintenance: false,
        isChecking: false,
        lastChecked: new Date(),
        checkCount: prev.checkCount + 1
      }));

      if (isDebugMode()) {
        console.log('✅ Serveur de nouveau disponible - Sortie du mode maintenance');
      }

      return false;
    } catch (error: any) {
      const isStillInMaintenance = error.status === 503;
      
      setStatus(prev => ({
        ...prev,
        isInMaintenance: isStillInMaintenance,
        isChecking: false,
        lastChecked: new Date(),
        checkCount: prev.checkCount + 1
      }));

      if (isDebugMode()) {
        console.log(`🔄 Vérification maintenance: ${isStillInMaintenance ? 'Toujours en maintenance' : 'Erreur différente'}`);
      }

      return isStillInMaintenance;
    }
  };

  /**
   * Force une vérification manuelle
   */
  const forceCheck = () => {
    return checkMaintenanceStatus();
  };

  /**
   * Marque manuellement comme étant en maintenance
   */
  const setMaintenanceMode = (inMaintenance: boolean) => {
    setStatus(prev => ({
      ...prev,
      isInMaintenance: inMaintenance,
      lastChecked: new Date()
    }));
  };

  // Vérification automatique périodique si on est en mode maintenance
  useEffect(() => {
    if (!status.isInMaintenance || !autoCheckInterval) return;

    const interval = setInterval(() => {
      checkMaintenanceStatus();
    }, autoCheckInterval);

    return () => clearInterval(interval);
  }, [status.isInMaintenance, autoCheckInterval]);

  // Vérification initiale si on est sur la page de maintenance
  useEffect(() => {
    if (window.location.pathname === '/maintenance') {
      setMaintenanceMode(true);
      // Faire une première vérification après 5 secondes
      setTimeout(checkMaintenanceStatus, 5000);
    }
  }, []);

  return {
    ...status,
    checkMaintenanceStatus: forceCheck,
    setMaintenanceMode
  };
}

export default useMaintenanceMode;